/**
 *  人员管理模块服务端、客户端数据转换
 */

import i18n from "@/i18n";
import { recursionTrans } from "../../common/util.js";
import { businessLangs } from "@/components/langSelect/languages.js";

function getStatusLabel(status) {
    const statusObj = { 0: i18n.t("zhiji.personnel.disablingLabel"), 1: i18n.t("zhiji.common.normal") };
    return statusObj[status] || "";
}

// 错误信息转换
function cvtRequestErrorData(error) {
    return {
        errCode: error.err_code,
        errMessage: error.err_msg,
        result: error.result
    };
}

/**********
 *  账号管理
 ***********/

// 账号管理列表请求参数
function cvtAccountParam(reqData) {
    return {
        member_id: reqData.accountId || "",
        name: reqData.accountName || "",
        email: reqData.email || "",
        page: reqData.page,
        size: reqData.size,
        group_id: reqData.group === "all" || reqData.group === undefined ? 0 : reqData.group
    };
}

// 账号新增保存请求参数
function cvtAccountSaveParam(reqData) {
    return {
        name: reqData.accountName,
        email: reqData.useEmail,
        role_id: reqData.roleId,
        groups: reqData.groups || 0,
        online_max_num: reqData.onlineMax,
        daily_issue_max_num: reqData.dayMax,
        member_id: reqData.memberId
    };
}

// 账号更新请求参数
function cvtAccountUpdateParam(reqData) {
    return {
        member_id: reqData.accountId,
        name: reqData.accountName,
        email: reqData.useEmail,
        role_id: reqData.roleId,
        groups: reqData.groups || 0,
        online_max_num: reqData.onlineMax,
        daily_issue_max_num: reqData.dayMax
    };
}

// 账号管理列表客户端数据格式
function accountData2Client(result) {
    let ret = {
        accountTotal: result.count,
        accountData: []
    };
    ret.accountData = result.list.map(item => {
        return {
            id: item.mid,
            userName: item.name,
            account: item.email,
            rowId: item.mid,
            userRole: item.role_name,
            userGroup: item.groups ? item.group_names : i18n.t("zhiji.personnel.defaultLable"),
            chatNum: item.online_max_num ? item.online_max_num : i18n.t("zhiji.personnel.defaultLable"),
            maxWork: item.daily_issue_max_num ? item.daily_issue_max_num : i18n.t("zhiji.personnel.defaultLable"),
            status: getStatusLabel(item.status),
            operate: getOperateArr(item),
            addData: {
                userName: item.name,
                account: item.email,
                userRole: item.role_id,
                userGroup: item.groups == "0" ? "all" : item.groups,
                chatNum: item.online_max_num,
                maxWork: item.daily_issue_max_num
            }
        };
    });
    return ret;
}

function getOperateArr(data) {
    if (data.status == 0) {
        return ["edit", "enable", "delete"];
    } else if (data.status == 1) {
        return ["edit", "disable", "delete"];
    }
    return [];
}

/**********
 *  分组管理
 ***********/

// 所有分组信息客户端数据格式
function allGroupOption2Client(result) {
    let ret = [
        {
            label: i18n.t("zhiji.personnel.defaultGroup"),
            value: "all"
        }
    ];

    ret.push(
        ...result.list.map(function(group) {
            return {
                label: group.group_name,
                value: group.group_id
            };
        })
    );

    return ret;
}

// 分组管理列表请求参数
function cvtGroupParam(reqData) {
    return {
        name: reqData.groupName || "",
        page: reqData.page,
        size: reqData.size
    };
}

// 分组新增保存请求参数
function cvtGroupSaveParam(reqData) {
    return {
        tag_id: reqData.tagId,
        group_name: reqData.groupName,
        users: reqData.users || "",
        lang: reqData.language,
        group_type: reqData.groupType,
        group_online_max_num: reqData.onlineMax || "",
        group_daily_issue_max_num: reqData.dayMax || ""
    };
}

// 组别更新请求参数
function cvtGroupUpdateParam(reqData) {
    return {
        tag_id: reqData.tagId,
        group_id: reqData.groupId,
        group_name: reqData.groupName,
        users: reqData.users || "",
        lang: reqData.language,
        group_type: reqData.groupType,
        group_online_max_num: reqData.onlineMax || "",
        group_daily_issue_max_num: reqData.dayMax || ""
    };
}

// 分组管理列表客户端数据格式
function groupData2Client(result) {
    let ret = {
        groupListTotal: result.count,
        groupData: []
    };
    let groupTypeObj = {
        0: i18n.t("zhiji.personnel.defaultGroup"),
        1: i18n.t("zhiji.personnel.groupManage.groupEditModal.workGroup"),
        2: i18n.t("zhiji.personnel.groupManage.groupEditModal.chatGroup"),
        3: i18n.t("zhiji.personnel.groupManage.groupEditModal.workGroup") + "/" + i18n.t("zhiji.personnel.groupManage.groupEditModal.chatGroup")
    };

    ret.groupData = result.list.map(item => {
        return {
            id: item.group_id,
            groupName: item.group_name,
            member: item.users_name,
            language: getCurLang(item.lang),
            rowId: item.group_id,
            classify: groupTypeObj[item.group_type],
            doMax: item.group_daily_issue_max_num,
            status: getStatusLabel(item.status),
            operate: getOperateArr(item),
            tagName: `${item.parent_tag_name} > ${item.tag_name}`,
            addData: {
                tagId: item.tag_id,
                groupName: item.group_name,
                member: item.users,
                language: item.lang,
                rowId: item.group_id,
                classify: item.group_type,
                doMax: item.group_daily_issue_max_num,
                chatMax: item.group_online_max_num
            }
        };
    });
    return ret;
}

function getCurLang(lang) {
    let curLang = businessLangs().filter(item => {
        return item.lang === lang;
    });

    return curLang.length > 0 ? i18n.t(curLang[0].i18nKey) : "";
}

/**********
 *  角色管理
 ***********/

// 所有角色信息客户端数据格式
function allRoleOption2Client(result) {
    let ret = [
        {
            label: i18n.t("zhiji.personnel.roleManage.roleEditModal.selectRoleTip"),
            value: ""
        }
    ];

    ret.push(
        ...result.list.map(function(group) {
            return {
                label: group.role_name,
                value: group.role_id
            };
        })
    );

    return ret;
}

// 角色管理列表请求参数
function cvtRoleListParam(reqData) {
    return {
        page: reqData.page,
        size: reqData.size
    };
}

// 角色管理列表客户端数据格式
function roleListData2Client(result) {
    let ret = {
        roleListTotal: result.count,
        roleData: []
    };
    ret.roleData = result.list.map(item => {
        return {
            id: item.role_id,
            roleName: item.role_name,
            perModule: item.permit_names,
            remarks: item.remark,
            rowId: item.role_id,
            status: getStatusLabel(item.status),
            operate: getOperateArr(item),
            addData: {
                roleName: item.role_name,
                perModule: item.permit,
                remarks: item.remark,
                rowId: item.role_id,
                permitId: item.special_permit
            }
        };
    });
    return ret;
}

// 角色新增保存请求参数
function cvtRoleSaveParam(reqData) {
    return {
        role_name: reqData.roleName,
        remark: reqData.remarkData,
        permit: reqData.permit
    };
}

// 角色更新请求参数
function cvtRoleUpdateParam(reqData) {
    return {
        openid: reqData.openid || "",
        role_id: reqData.roleId,
        role_name: reqData.roleName,
        remark: reqData.remarkData,
        permit: reqData.permit
    };
}

// 权限列表客户端数据格式
function permitList2Client(result) {
    return recursionTrans(
        result,
        function(permitData) {
            return {
                id: "" + permitData.id,
                label: permitData.name,
                level: permitData.hierarchy,
                text: permitData.text,
                parentId: permitData.pid
            };
        },
        "nodes",
        "children"
    );
}

export default {
    cvtRequestErrorData,
    cvtAccountParam,
    accountData2Client,
    cvtGroupParam,
    groupData2Client,
    cvtRoleListParam,
    roleListData2Client,
    allGroupOption2Client,
    allRoleOption2Client,
    cvtAccountSaveParam,
    cvtAccountUpdateParam,
    cvtGroupSaveParam,
    cvtGroupUpdateParam,
    cvtRoleSaveParam,
    cvtRoleUpdateParam,
    permitList2Client
};
